/**
 * ToolbarButton Component
 * Story 6.1: Implement Unified Toolbar Wrapper
 *
 * Reusable toolbar button with tooltip showing action name and keyboard shortcut.
 * AC-6.1.5: Platform-aware modifier display (macOS: ⌘, Windows/Linux: Ctrl)
 */
import { useState, type ReactNode, type ButtonHTMLAttributes } from 'react';
import { usePlatform } from '../../hooks/usePlatform';

interface ToolbarButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Icon to display */
  icon: ReactNode;
  /** Accessible label for screen readers */
  label: string;
  /** Keyboard shortcut (use 'Mod' for Cmd/Ctrl) - e.g., 'Mod+Z' */
  shortcut?: string;
  /** Whether button is active/toggled */
  active?: boolean;
}

/**
 * Formats shortcut string for platform-specific display
 * Replaces 'Mod' with ⌘ (Mac) or Ctrl (Windows)
 */
function formatShortcut(shortcut: string, modifierSymbol: string): string {
  return shortcut
    .replace(/Mod/g, modifierSymbol)
    .replace(/Shift/g, '⇧')
    .replace(/\+/g, '');
}

export function ToolbarButton({
  icon,
  label,
  shortcut,
  active = false,
  disabled = false,
  className = '',
  ...props
}: ToolbarButtonProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const { modifierSymbol } = usePlatform();

  const formattedShortcut = shortcut ? formatShortcut(shortcut, modifierSymbol) : null;

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        className={`
          toolbar-icon-btn w-8 h-8 flex items-center justify-center rounded
          transition-colors border-none cursor-pointer
          ${active ? 'bg-[var(--hover-bg)] text-[var(--toolbar-icon-active,var(--tenant-primary))]' : ''}
          ${disabled ? 'text-[var(--toolbar-icon-disabled,var(--text-muted))] cursor-not-allowed' : ''}
          ${!disabled && !active ? 'text-[var(--toolbar-icon-color,var(--text-secondary))] hover:bg-[var(--hover-bg)] hover:text-[var(--toolbar-icon-hover,var(--text-primary))]' : ''}
          ${className}
        `}
        aria-label={label}
        aria-disabled={disabled ? 'true' : undefined}
        disabled={disabled}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        {...props}
      >
        <span className="w-5 h-5 flex items-center justify-center">
          {icon}
        </span>
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div
          role="tooltip"
          className="
            absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50
            px-2 py-1 rounded text-xs whitespace-nowrap
            bg-[var(--surface-2,#333)] text-[var(--text-primary,#fff)]
            shadow-lg pointer-events-none
          "
        >
          <div className="font-medium">{label}</div>
          {formattedShortcut && (
            <div className="text-[var(--text-muted,#999)] mt-0.5">
              {formattedShortcut}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
